import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Progress,
  VStack,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getUsefulVars } from '../../handlers/helpers';
import { onLink } from '../../handlers';

const DiscInventory = ({ props,onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({ D: 0, I: 0, S: 0, C: 0 });
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      text: "When I'm in a meeting, I like to get straight to the point",
      example: "I'd rather skip the small talk and dive into what we need to accomplish",
      type: "D"
    },
    {
      text: "I get energized when I'm around other people",
      example: "Being at social events or team gatherings makes me feel alive",
      type: "I"
    },
    {
      text: "I prefer when things stay consistent and predictable",
      example: "I'm most comfortable when I know what to expect each day",
      type: "S"
    },
    {
      text: "I pay close attention to the little details",
      example: "I naturally notice when something is slightly off or incorrect",
      type: "C"
    },
    {
      text: "When there's a problem, I'm quick to jump in and take charge",
      example: "I don't wait for someone else to step up - I start solving it right away",
      type: "D"
    },
    {
      text: "I love sharing ideas and getting others excited about them",
      example: "I enjoy bringing enthusiasm to group discussions and brainstorming",
      type: "I"
    },
    {
      text: "I'm the person people come to when they need someone to listen",
      example: "Friends and coworkers know I'll hear them out without judgment",
      type: "S"
    },
    {
      text: "I like to follow proven methods and established guidelines",
      example: "If there's a best practice or system in place, I prefer to stick with it",
      type: "C"
    },
    {
      text: "I thrive in competitive situations",
      example: "Whether it's sports, work goals, or games, I love the challenge of winning",
      type: "D"
    },
    {
      text: "I'm naturally optimistic and see the bright side of things",
      example: "Even when things go wrong, I tend to focus on the positive possibilities",
      type: "I"
    },
    {
      text: "I'm happy to support others and help them succeed",
      example: "I get satisfaction from being part of the team and helping colleagues reach their goals",
      type: "S"
    },
    {
      text: "I think things through carefully before making decisions",
      example: "I like to gather information and analyze all the options first",
      type: "C"
    },
    {
      text: "I don't mind difficult conversations or confronting issues head-on",
      example: "If something needs to be addressed, I'm comfortable bringing it up directly",
      type: "D"
    },
    {
      text: "I enjoy collaborating and working closely with others",
      example: "Group projects are more fun for me than working alone",
      type: "I"
    },
    {
      text: "I value stability and don't like sudden changes",
      example: "I prefer gradual transitions rather than unexpected shifts in plans",
      type: "S"
    },
    {
      text: "I rely on facts and data to guide my thinking",
      example: "I want to see the numbers and evidence before drawing conclusions",
      type: "C"
    },
    {
      text: "I make decisions quickly and confidently",
      example: "Once I have enough information, I'm ready to move forward without hesitation",
      type: "D"
    },
    {
      text: "I'm comfortable being the center of attention",
      example: "I don't mind presenting to groups or being in the spotlight",
      type: "I"
    },
    {
      text: "I stay calm and level-headed, even under pressure",
      example: "When things get chaotic, I'm the steady presence that keeps things balanced",
      type: "S"
    },
    {
      text: "I'm methodical and like having clear processes to follow",
      example: "I appreciate when there's a step-by-step approach to getting things done",
      type: "C"
    },
  ];

  const typeDescriptions = {
    D: {
      name: "Dominance",
      fullName: "Dominant",
      description: "Direct, results-oriented, and decisive. You are assertive, competitive, and focused on achieving goals. You thrive on challenges and taking charge of situations.",
      strengths: "Leadership, quick decision-making, problem-solving, results focus",
      workStyle: "Fast-paced, direct communication, goal-oriented, independent"
    },
    I: {
      name: "Influence",
      fullName: "Influential",
      description: "Outgoing, enthusiastic, and people-oriented. You are optimistic, persuasive, and excel at building relationships. You bring energy and positivity to teams.",
      strengths: "Communication, persuasion, team building, creativity",
      workStyle: "Collaborative, expressive, optimistic, relationship-focused"
    },
    S: {
      name: "Steadiness",
      fullName: "Steady",
      description: "Patient, supportive, and reliable. You value stability, cooperation, and helping others. You are a great team player who maintains harmony and consistency.",
      strengths: "Patience, listening, loyalty, consistency",
      workStyle: "Steady pace, supportive, team-oriented, predictable environment"
    },
    C: {
      name: "Conscientiousness",
      fullName: "Conscientious",
      description: "Analytical, accurate, and detail-oriented. You value quality, precision, and systematic approaches. You excel at analyzing data and ensuring high standards.",
      strengths: "Accuracy, analysis, planning, quality focus",
      workStyle: "Methodical, data-driven, precise, structured environment"
    }
  };

  const discPatterns = {
    "D": {
      name: "The Director",
      detailed: "You're a natural-born leader who doesn't wait around. You see what needs to happen and you make it happen - no fluff, no excuses. People know where they stand with you because you're direct and honest. You thrive when you're in control and tackling big challenges. While others are still discussing, you're already three steps ahead solving the problem.",
      strengths: "Making tough calls when everyone else is hesitating, cutting through confusion to find solutions, pushing teams to achieve what they didn't think was possible",
      challenges: "You might bulldoze over others' feelings without realizing it, or make decisions so quickly that your team feels left behind. Sometimes slowing down just a little helps everyone stay on board.",
      idealEnvironment: "Give you challenging goals, the freedom to achieve them your way, and stand back - you'll deliver results faster than anyone expected"
    },
    "I": {
      name: "The Socializer",
      detailed: "You light up a room the moment you walk in. People are naturally drawn to your energy and optimism - you make work feel less like work and more like a collaboration among friends. You have a gift for seeing the best in people and situations, and you're the first to celebrate wins (big or small). Your enthusiasm is genuinely contagious.",
      strengths: "Getting people excited about ideas they were skeptical about, building connections that turn coworkers into genuine collaborators, keeping morale high even when times are tough",
      challenges: "You might struggle with the boring details or spreadsheets, and your optimism can sometimes gloss over real problems that need addressing. Not everyone loves being in the spotlight as much as you do.",
      idealEnvironment: "Put you on teams where you can brainstorm, connect with others, and bring energy - just pair you with someone who loves tackling the follow-through details"
    },
    "S": {
      name: "The Supporter",
      detailed: "You're the steady heartbeat of any team. While others are chasing the spotlight or racing ahead, you're the one making sure everyone's okay and nothing falls through the cracks. You're genuinely patient - like, actually patient, not just pretending. People come to you because they know you'll really listen without judgment or rushing them.",
      strengths: "Being the reliable person everyone can count on, creating an environment where people actually want to work together, staying calm when everything around you is chaotic",
      challenges: "Change can feel overwhelming to you, even when it's necessary. You might say 'yes' when you should say 'no' because you don't want to disappoint anyone. Standing up for yourself can feel uncomfortable.",
      idealEnvironment: "You thrive in stable teams where consistency matters, people value loyalty, and there's time to build real relationships - not constant upheaval and urgent deadlines"
    },
    "C": {
      name: "The Analyzer",
      detailed: "You notice what others miss. That tiny error in the spreadsheet? You caught it. That assumption everyone's making without data? You're questioning it. You have incredibly high standards - mostly for yourself - and you genuinely care about getting things right, not just done. Quality isn't just a buzzword to you; it's how you operate.",
      strengths: "Catching mistakes before they become disasters, thinking through all the angles everyone else missed, creating systems that actually work (not just look good on paper)",
      challenges: "Perfectionism can trap you in endless analysis, and you might be harder on yourself (and others) than necessary. Sometimes 'good enough and done' beats 'perfect and still in progress.'",
      idealEnvironment: "You need space to think deeply, access to data and facts, and time to do things properly - rushed decisions and 'good enough' environments will drain you"
    },
    "DI": {
      name: "The Inspirational Leader",
      detailed: "You're a powerhouse combination of 'let's do this NOW' energy and 'let's do this TOGETHER' charisma. When you walk into a room with an idea, people don't just listen - they get excited and want to help make it happen. You're not just leading; you're inspiring. You make ambitious goals feel achievable and fun at the same time.",
      strengths: "Rallying people around big visions, making fast decisions while bringing everyone along, turning 'impossible' goals into team victories",
      challenges: "You might blow past important details in your rush to make things happen, and your high energy can accidentally leave more cautious team members in the dust. Not everyone processes at your speed.",
      idealEnvironment: "Put you in charge of ambitious projects with great people, give you room to move fast, and watch as you turn vision into reality while keeping everyone energized"
    },
    "DC": {
      name: "The Challenger",
      detailed: "You combine relentless drive with razor-sharp standards. You don't just want results - you want excellent results, backed by data, done right. You're not afraid to question conventional wisdom or push back when something doesn't meet your standards. While others accept 'good enough,' you're asking 'but could it be better?'",
      strengths: "Elevating quality standards, making strategic decisions based on actual data not gut feelings, pushing organizations toward genuine excellence",
      challenges: "Your high standards can feel intimidating or even critical to others. You might move so fast and expect so much that people struggle to keep up or feel they'll never meet your expectations.",
      idealEnvironment: "You thrive where both speed and quality matter, where data-driven decisions are valued, and where you can challenge the status quo without being seen as difficult"
    },
    "DS": {
      name: "The Coordinator",
      detailed: "You're the rare leader who can be tough on goals but gentle with people. You push for results while genuinely caring about your team's wellbeing. You understand that sustainable success means balancing urgency with stability, achievement with relationships. People trust you because you're both decisive and considerate.",
      strengths: "Building teams that deliver consistently without burning out, making progress while keeping everyone on board, knowing when to push and when to support",
      challenges: "You might feel torn between driving results and maintaining harmony, especially when those goals conflict. Choosing between what's best for outcomes versus what's best for people can create internal stress.",
      idealEnvironment: "You need environments where both results and relationships matter equally - cultures that value achievement but won't sacrifice people to get there"
    },
    "IS": {
      name: "The Counselor",
      detailed: "You bring warmth, patience, and genuine care to everything you do. People feel safe opening up to you because you're both enthusiastic and understanding. You're the team member who notices when someone's struggling and actually takes time to help. Your combination of friendliness and steadiness makes you a natural mentor.",
      strengths: "Building trust that turns coworkers into true teammates, creating positive environments where people actually want to come to work, supporting others through change with patience and encouragement",
      challenges: "Conflict makes you uncomfortable, so you might avoid necessary difficult conversations. Your desire to keep everyone happy can lead to overcommitting or not speaking up when something bothers you.",
      idealEnvironment: "You thrive in collaborative, supportive cultures that value relationships, give you time to connect with others, and appreciate the positive atmosphere you create"
    },
    "IC": {
      name: "The Assessor",
      detailed: "You're enthusiastic about ideas but meticulous about execution - a rare and valuable combination. You bring energy to problem-solving while also caring deeply about getting details right. You can present complex information in engaging ways, making data interesting instead of just dumping spreadsheets on people.",
      strengths: "Making careful analysis actually interesting, ensuring quality while keeping people engaged, balancing creative thinking with practical execution",
      challenges: "You might feel pulled between your social, creative side and your analytical, detail-oriented side. Routine tasks can bore you even though you know they're important.",
      idealEnvironment: "You need work that combines people interaction with meaningful analysis - somewhere between pure data work and pure socializing, where quality and relationships both matter"
    },
    "SC": {
      name: "The Planner",
      detailed: "You're the person who actually reads the manual and creates systems that work. You value consistency, reliability, and doing things right - not just fast. Change doesn't excite you unless there's a clear, well-thought-out plan. Your attention to both process and quality means things done your way stay done.",
      strengths: "Creating reliable systems, following through on commitments, producing consistent high-quality work, being the dependable foundation others can count on",
      challenges: "You might resist needed changes or innovations because the current system 'works fine.' Your careful, methodical approach can feel slow to others who want to move faster.",
      idealEnvironment: "You thrive in stable, organized environments where quality and consistency are genuinely valued, where you have time to do things properly, and change happens thoughtfully"
    },
    "DIS": {
      name: "The Motivator",
      detailed: "You're a triple threat of action, enthusiasm, and people skills. You don't just want to win - you want everyone to win together, and you want it to be exciting. You're the leader who makes teams feel like they're part of something bigger. Your energy, drive, and charisma create momentum that pulls others forward.",
      strengths: "Inspiring teams to achieve more than they thought possible, creating excitement around change initiatives, leading transformation with both urgency and optimism",
      challenges: "Details and follow-through aren't your strong suits - you'd rather start the next exciting thing. Your fast pace and high energy can overwhelm people who need more time or stability.",
      idealEnvironment: "Put you at the front of important initiatives where you can rally people, move fast, and create change - just ensure someone's handling the implementation details"
    },
    "DIC": {
      name: "The Strategist",
      detailed: "You're the rare combination of visionary and executor, big picture and detail-oriented. You don't just dream big - you create actual plans to make it happen, backed by data and logic. You're persuasive because you've thought it through. You combine strategic thinking with the confidence to act on it.",
      strengths: "Creating data-backed strategies, convincing others with both logic and vision, ensuring ambitious plans are actually achievable, balancing innovation with quality",
      challenges: "Your high standards and drive for results can make you demanding. You might struggle when speed conflicts with perfection, or when you need to choose between what's strategic versus what's immediately achievable.",
      idealEnvironment: "You thrive in environments that value strategic thinking, appreciate thorough planning, and give you autonomy to implement well-considered initiatives with quality"
    },
    "DSC": {
      name: "The Implementer",
      detailed: "You're the rare professional who can see the big picture, care about people, maintain quality standards, AND actually get things done. You don't just plan or dream - you implement sustainably. You understand that lasting success requires balancing multiple priorities: results, relationships, quality, and stability.",
      strengths: "Creating sustainable results, implementing changes thoughtfully, balancing competing priorities, building systems that work for everyone",
      challenges: "With strengths across all areas, you might feel tension when priorities conflict. Rapid change can be stressful because you see all the implications others miss.",
      idealEnvironment: "You thrive in mature organizations that value balanced leadership, sustainable growth, and long-term success over quick wins - where all aspects of work matter"
    },
    "ISC": {
      name: "The Harmonizer",
      detailed: "You create environments where people genuinely enjoy working while maintaining high standards. You're the rare person who is both warm and detail-oriented, supportive and quality-focused. You build strong relationships AND ensure work gets done right. People trust you because you combine kindness with competence.",
      strengths: "Building positive, high-quality work environments, supporting others while maintaining standards, creating cultures where people and excellence coexist",
      challenges: "You might avoid conflict even when addressing it would improve things. Your careful, relationship-focused approach can feel slow in fast-paced, highly competitive environments.",
      idealEnvironment: "You thrive in collaborative cultures that value both people and quality work, where relationships matter as much as results, and where there's time to do things properly"
    },
    "DISC": {
      name: "The Versatile Leader",
      detailed: "You're impressively adaptable, showing balance across all behavioral styles. You can read situations and adjust your approach - being directive when needed, supportive when helpful, enthusiastic when appropriate, or analytical when required. This flexibility makes you effective with diverse personality types and in varied situations.",
      strengths: "Adapting to different situations and people, understanding multiple perspectives, leading diverse teams, switching modes as needed",
      challenges: "Without a dominant style, you might lack a clear 'brand' - people may find you harder to read. You can experience internal tension when different approaches conflict, unsure which 'version of you' to bring.",
      idealEnvironment: "You excel in diverse, dynamic environments requiring flexibility, where you interact with varied personality types and face different challenges requiring different approaches"
    }
  };

  const handleAnswer = (rating) => {
    const questionType = questions[currentQuestion].type;

    // Score based on agreement level
    let points = 0;
    if (rating === 5) points = 2;      // Strongly Agree
    else if (rating === 3) points = 1; // Agree
    else if (rating === 1) points = 0; // Neutral
    else if (rating === -1) points = 0; // Disagree
    else if (rating === -3) points = 0; // Strongly Disagree

    setScores(prev => ({
      ...prev,
      [questionType]: prev[questionType] + points
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getTopTypes = () => {
    const sortedScores = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .filter(([, score]) => score > 0);
    return sortedScores;
  };

  const getDISCPattern = () => {
    const topTypes = getTopTypes();
    if (topTypes.length === 0) return "DISC";

    // Get types that are within 20% of the highest score
    const maxScore = topTypes[0][1];
    const threshold = maxScore * 0.8;
    const dominantTypes = topTypes
      .filter(([, score]) => score >= threshold)
      .map(([type]) => type)
      .join('');

    return dominantTypes || topTypes[0][0];
  };

  const handleSaveAndContinue = (props) => {
    const topTypes = getTopTypes();
    const discPattern = getDISCPattern();

    const uv = getUsefulVars(props);
    const resume = uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex];
    if(resume['new-aptitude'] === undefined) {
      resume['new-aptitude'] = [];
      resume['new-aptitude'].push({});
    }
    resume['new-aptitude'][0].disc_inventory = discPattern;

    onLink(false, props.formikProps, props.stateProps, 'new-aptitude',0,'section',props.stateProps.sectionData.resumeIndex);

  };

  const chartData = Object.entries(scores).map(([type, score]) => ({
    type: typeDescriptions[type].name,
    score: score,
    code: type
  }));

  const colors = {
    D: '#dd1198',  // Pink - Dominant
    I: '#ff9800',  // Orange - Influential
    S: '#252f59',  // Navy - Steady
    C: '#6c757d'   // Gray - Conscientious
  };

  if (showResults) {
    const topTypes = getTopTypes();
    const discPattern = getDISCPattern();
    const patternInfo = discPatterns[discPattern] || {
      name: "The Balanced Profile",
      description: "You show balanced traits across multiple styles"
    };

    return (
      <Container maxW="container.lg" py={6}>
        <Box bg="white" p={6} borderRadius="lg" shadow="lg">
          <Heading as="h2" size="xl" textAlign="center" mb={6} color="#252f59">
            Your DISC Assessment Results
          </Heading>

          <Box borderLeftWidth="4px" borderColor="#dd1198" bg="#fff5f7" p={4} mb={6} borderRadius="md">
            <Box mb={4}>
              <Text fontSize="lg" fontWeight="semibold" color="#252f59">
                Your DISC Profile: <Text as="span" fontSize="2xl" color="#dd1198">{discPattern}</Text>
              </Text>
              <Text fontSize="xl" mt={2} fontWeight="bold" color="#ff9800">
                {patternInfo.name}
              </Text>
            </Box>

            <Text color="gray.700" mb={4}>{patternInfo.detailed}</Text>

            <VStack align="stretch" spacing={2} fontSize="sm">
              <Text color="gray.700">
                <Text as="strong" color="#dd1198">What you bring:</Text> {patternInfo.strengths}
              </Text>
              <Text color="gray.700">
                <Text as="strong" color="#ff9800">Watch out for:</Text> {patternInfo.challenges}
              </Text>
              <Text color="gray.700">
                <Text as="strong" color="#252f59">Where you thrive:</Text> {patternInfo.idealEnvironment}
              </Text>
            </VStack>
          </Box>

          <Box mb={8}>
            <Heading as="h3" size="lg" mb={4} color="#252f59">
              Score Distribution
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[entry.type]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {topTypes.length > 1 && (() => {
            const highestScore = topTypes[0][1];
            const secondScore = topTypes[1][1];
            const scoreDiff = highestScore - secondScore;
            if (scoreDiff <= 2) {
              return (
                <Alert status="warning" variant="left-accent" borderRadius="lg" mt={6} bg="#fff9e6" borderColor="#ff9800" borderWidth="2px">
                  <AlertIcon />
                  <AlertDescription fontSize="sm" color="#252f59">
                    <Text as="strong">💡 Balanced Profile:</Text> Your top scores are very close ({topTypes[0][0]}: {highestScore} vs {topTypes[1][0]}: {secondScore}). This means you demonstrate a blend of these behavioral styles and can adapt your approach based on the situation.
                  </AlertDescription>
                </Alert>
              );
            }
            return null;
          })()}

          <Button
            onClick={() => {
              handleSaveAndContinue(props);
            }}
            w="full"
            mt={6}
            bg="#dd1198"
            color="white"
            py={6}
            fontSize="md"
            width="260px"
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Save Results & Continue
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={6}>
      <Box bg="white" p={6} borderRadius="lg" shadow="lg">
        <Heading as="h1" size="xl" textAlign="center" mb={2} color="#252f59">
          DISC Behavioral Assessment
        </Heading>
        <Text textAlign="center" color="gray.600" mb={6}>
          Discover your behavioral style. Think about how you naturally act in work situations and answer honestly.
        </Text>

        <Box mb={6}>
          <Flex justify="space-between" fontSize="sm" color="gray.600" mb={2}>
            <Text>Question {currentQuestion + 1} of {questions.length}</Text>
            <Text>{Math.round(((currentQuestion) / questions.length) * 100)}%</Text>
          </Flex>
          <Progress
            value={((currentQuestion) / questions.length) * 100}
            size="sm"
            colorScheme="pink"
            bg="gray.200"
            borderRadius="full"
          />
        </Box>

        <Flex
          bg="gray.50"
          p={6}
          borderRadius="lg"
          mb={6}
          minH="32"
          align="center"
          justify="center"
          direction="column"
        >
          <Text fontSize="xl" textAlign="center" fontWeight="semibold" color="#252f59" mb={2}>
            {questions[currentQuestion].text}
          </Text>
          <Text fontSize="sm" textAlign="center" fontStyle="italic" color="gray.600">
            {questions[currentQuestion].example}
          </Text>
        </Flex>

        <VStack spacing={3}>
          <Button
            onClick={() => handleAnswer(5)}
            w="full"
            bg="#ff9800"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Agree
          </Button>
          <Button
            onClick={() => handleAnswer(3)}
            w="full"
            bg="#dd1198"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Agree
          </Button>
          <Button
            onClick={() => handleAnswer(1)}
            w="full"
            bg="#252f59"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Neutral
          </Button>
          <Button
            onClick={() => handleAnswer(-1)}
            w="full"
            bg="#6c757d"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Disagree
          </Button>
          <Button
            onClick={() => handleAnswer(-3)}
            w="full"
            bg="#e9ecef"
            color="#252f59"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Disagree
          </Button>
        </VStack>
      </Box>
      <Button
        w="full"
        mt={6}
        bg="#dd1198"
        color="white"
        py={6}
        fontSize="md"
        width="260px"
        fontWeight="semibold"
        borderRadius="lg"
        _hover={{ opacity: 0.9 }}  className="aptitudeButton"
        onClick={() => onLink(false,props.formikProps,props.stateProps,'new-aptitude')}>
        Return
      </Button>
    </Container>
  );
};

export default DiscInventory;